import { Locale } from '@core/constants/locales.constants';
import { FooterBlock, LinkList } from '@graphql/index';
import {
  ContactDetailsProps,
  FooterBlockProps,
  LinkListItemProps,
  SocialLinkProps,
} from '@stadionhq-v3/base-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { isLink } from '@core/types/typeGuards';
import { Email, Location, Phone } from '@components/icons';
import { linkTransform } from '../atoms';
import userSponsorGroupTransform from './sponsorGroup.transform';

export type FooterBlockTransformArgs = {
  data: CleanContentful<FooterBlock>;
  locale: Locale;
  hideAgeSensitiveContent: boolean;
  dictionary: {
    usefulLinks: string;
    appStoreDetailsTitle: string;
    appStoreDetailsSummary: string;
  };
  globalSettings?: {
    appStoreLinkApple?: string;
    appStoreLinkAndroid?: string;
  };
};

type SocialChannelsField =
  | {
      title?: string;
      summary?: string;
      channels?: SocialLinkProps[];
    }
  | undefined
  | null;

const buildGroupLinks = (
  group: SocialChannelsField,
  groupIndex: 1 | 2 | 3
): SocialLinkProps[] => {
  const channels = group?.channels;
  if (!channels?.length) return [];

  const groupClass = `socialLinkGroup${groupIndex}`;
  const isFirstGroup = groupIndex === 1;
  const groupSummary = group?.summary;

  return channels.map((link, index) => {
    const showStartMarker = !isFirstGroup && index === 0;
    const classNames = [
      ...(link.classNames ?? []),
      groupClass,
      ...(showStartMarker ? ['socialLinkGroupStart'] : []),
    ];

    if (showStartMarker && groupSummary) {
      return {
        ...link,
        classNames,
        nativeProps: {
          ...link.nativeProps,
          'data-group-summary': groupSummary,
        },
      } as SocialLinkProps;
    }

    return { ...link, classNames };
  });
};

export const footerBlockTransform = ({
  data: {
    id,
    sponsorsGroups,
    tierOneSponsors,
    socialChannels,
    socialChannels2,
    socialChannels3,
    usefulLinksGroups,
    legalLinks,
    copyrightStatement,
    contactSectionTitle,
    contactDetailsPanel,
  },
  locale,
  dictionary,
  globalSettings,
  hideAgeSensitiveContent,
}: FooterBlockTransformArgs): FooterBlockProps => ({
  id: id as string,
  sponsors: sponsorsGroups && {
    tierOne:
      tierOneSponsors &&
      userSponsorGroupTransform(
        tierOneSponsors,
        hideAgeSensitiveContent,
        locale
      ),
    sponsorsGroups: sponsorsGroups?.map(
      (sponsorsGroup) =>
        sponsorsGroup &&
        userSponsorGroupTransform(
          sponsorsGroup,
          hideAgeSensitiveContent,
          locale
        )
    ),
  },
  socialLinks: {
    title: socialChannels?.title || '',
    summary: socialChannels?.summary || '',
    links: [
      ...buildGroupLinks(socialChannels, 1),
      ...buildGroupLinks(socialChannels2, 2),
      ...buildGroupLinks(socialChannels3, 3),
    ],
  },
  appPromo:
    globalSettings?.appStoreLinkApple || globalSettings?.appStoreLinkAndroid
      ? {
          title: dictionary.appStoreDetailsTitle,
          summary: dictionary.appStoreDetailsSummary,
          appleStoreLink: globalSettings?.appStoreLinkApple,
          googlePlayStoreLink: globalSettings?.appStoreLinkAndroid,
        }
      : undefined,
  usefulLinks: usefulLinksGroups?.length
    ? {
        title: dictionary.usefulLinks,
        linkGroups:
          usefulLinksGroups?.map((linkList: CleanContentful<LinkList>) => ({
            ...linkList,
            links: (linkList?.links
              ?.map((link) => ({
                ...(link?.url ? linkTransform(link, locale) : undefined),
              }))
              .filter(isLink) ?? []) as LinkListItemProps[],
          })) ?? [],
      }
    : undefined,
  copyrightStatement: copyrightStatement || '',
  legalLinks: legalLinks?.filter(isLink).map((link) => ({
    ...linkTransform(link, locale),
    size: 'small',
    nativeProps: {},
  })),
  contactDetails: {
    title: contactSectionTitle || '',
    addressBlocks: contactDetailsPanel?.slice(0, 2).map((contact) => ({
      ...contact,
      addressIcon: <Location />,
      numberIcon: <Phone />,
      emailIcon: <Email />,
    })) as ContactDetailsProps[],
  },
});

export default footerBlockTransform;
